import { QueryClient, useQuery } from '@tanstack/react-query';
import request from '@utils/request';
import moment from 'moment';
import { ConfigurationGroupType } from 'shared-types/SpaPackage';
import { SPA_PACKAGE_TIMESLOT_AVAILABILITY_TIMEOUT } from './consts';

/**
 * Folds fetched timeslots into the package's static configuration groups.
 * Shared so callers that fetch through useQueries get the same shape as the hook.
 */
export function mergeConfigGroupTimeslots(
  configurationGroups: any,
  fetchedConfigGroups: any
): ConfigurationGroupType[] {
  const safeConfigGroups: any[] = Array.isArray(configurationGroups) ? configurationGroups : [];

  const cleanConfigGroups = safeConfigGroups.map((item) => {
    const { options, ...rest } = item;
    const cleanOptions = (Array.isArray(options) ? options : []).map(
      ({ timeslots: _, ...restOption }) => restOption
    );
    return { ...rest, options: cleanOptions };
  });

  const updatedConfigGroups: ConfigurationGroupType[] = [];
  (Array.isArray(fetchedConfigGroups) ? fetchedConfigGroups : []).forEach(
    ({ id, options, isBookable }) => {
      const currentConfig = cleanConfigGroups.find((config) => config.id === id);
      if (currentConfig) {
        currentConfig.isBookable = isBookable;
        const fetchedOptions = Array.isArray(options) ? options : [];
        const currentConfigOptions = (currentConfig.options || []).map((confOpt) => {
          const timeslots = fetchedOptions.find((opt) => opt.id === confOpt.id)?.timeslots;
          if (timeslots) return { ...confOpt, timeslots };
          else return confOpt;
        });
        updatedConfigGroups.push({ ...currentConfig, options: currentConfigOptions });
      }
    }
  );

  const updatedIds = updatedConfigGroups.map((conf) => conf.id);
  const unchangedSpaPackageConfigGroups = safeConfigGroups.filter(
    (cg) => !updatedIds.includes(cg.id)
  );

  return [...updatedConfigGroups, ...unchangedSpaPackageConfigGroups] as ConfigurationGroupType[];
}

export function useGetSpaPackageConfigGroups(
  configurationGroups: any,
  propertyId: number,
  spaPackageId: number,
  arrivalDate: string,
  quantity: number,
  startTime?: string,
  endTime?: string
) {
  const {
    data: configGroups,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: getSpaPackageConfigGroupsKey(
      propertyId,
      spaPackageId,
      arrivalDate,
      quantity,
      startTime,
      endTime
    ),
    queryFn: () =>
      getSpaPackageConfigGroups(
        propertyId,
        spaPackageId,
        arrivalDate,
        quantity,
        startTime,
        endTime
      ),
    cacheTime: 60000,
    staleTime: 60000,
    enabled: !!spaPackageId && !!startTime,
  });

  if (error) {
    return {
      data: [],
      isLoading,
      isFetching,
      error: true,
    };
  }

  if (!configurationGroups && !isLoading) {
    return {
      data: [],
      isLoading,
      isFetching,
      error: false,
    };
  }
  if (configGroups) {
    return {
      data: mergeConfigGroupTimeslots(configurationGroups, configGroups),
      isLoading,
      isFetching,
      error: false,
    };
  } else
    return {
      data: [] as ConfigurationGroupType[],
      isLoading,
      isFetching,
      error: false,
    };
}

export const getSpaPackageConfigGroupsKey = (
  propertyId: number,
  spaPackageId: number,
  arrivalDate: string,
  quantity: number,
  startTime?: string,
  endTime?: string
) => [
  'spaPackage',
  'configGroups',
  propertyId,
  spaPackageId,
  arrivalDate,
  quantity,
  startTime,
  endTime,
];

export const getSpaPackageConfigGroups = async (
  propertyId: number,
  spaPackageId: number,
  arrivalDate: string,
  quantity: number,
  startTime?: string,
  endTime?: string
) => {
  const date = moment(arrivalDate).format('YYYY-MM-DD');
  const { data } = await request.get(
    `${propertyId}/spa-packages/${spaPackageId}/configuration-groups/${date}/timeslots`,
    {
      params: {
        quantity,
        'start-time': startTime,
        'end-time': endTime,
      },
      timeout: SPA_PACKAGE_TIMESLOT_AVAILABILITY_TIMEOUT,
    }
  );

  return data;
};
export function useInvalidateSpaPackageConfigGroups(queryClient: QueryClient) {
  return queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey.includes('spaPackage') && query.queryKey.includes('configGroups'),
  });
}
